import { useHubspotCookieBannerFocus, useLogin } from '@/hooks';
import { useState } from 'react';
import { TitleLargeEmphasized, TextInput, ButtonPrimaryLarge, BodyMedium } from '@/design-system';
import { useNavigate } from 'react-router-dom';
import emailIsValidish from '@/utils/formUtils';
import { APP_ROUTES, EMAIL_STRUCTURE_ERROR } from '@/config/constants';
import { openLoginDrawerVar } from '@/graphql/state';
import { useReactiveVar } from '@apollo/client';
import { PageTitleHelmet } from '@/components';
import colors from '@/theme/colors';
import styles from './Login.styles';

export const LOOK_UP_ERROR =
  'Unfortunately, we were unable to find your Peachjar account. Check your email address and try again or sign up for an account.';

interface Props {
  pageTitle?: string;
}

const FORM_INSTRUCTIONS_ID = 'loginFormInstructions';

export const Login: React.FC<Props> = ({ pageTitle = 'Log In' }) => {
  const [email, setEmail] = useState('');
  const [magicLinkFailed, setMagicLinkFailed] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const { requestMagicLink } = useLogin();
  const openLoginDrawer = useReactiveVar(openLoginDrawerVar);
  const emailErrorText: string = magicLinkFailed ? LOOK_UP_ERROR : EMAIL_STRUCTURE_ERROR;
  const navigate = useNavigate();
  const isEmailValid = emailIsValidish(email);

  useHubspotCookieBannerFocus();

  const onEmailInputChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const checkKeyAndSubmitEmail = (event?: { key: string; preventDefault: () => void }) => {
    if (event && Object.prototype.hasOwnProperty.call(event, 'key') && event.key === 'Enter') {
      event.preventDefault();
      onSubmit();
    }
  };

  const hideLoginDrawer = () => {
    if (openLoginDrawer) {
      openLoginDrawerVar(false);
    }
  };

  const onSubmit = async () => {
    if (!isEmailValid) {
      setHasError(true);
      const emailInputElement = document.getElementById('emailInput') as HTMLInputElement;
      emailInputElement?.focus();
      return;
    }

    const magicLinkGenerated = await requestMagicLink(email);
    if (!magicLinkGenerated) {
      setMagicLinkFailed(true);
    }
  };

  const onSignUp = () => {
    hideLoginDrawer();
    navigate(APP_ROUTES.signUp);
  };

  return (
    <main className={styles.container}>
      <PageTitleHelmet>Login</PageTitleHelmet>
      <form onSubmit={(e: any) => e.preventDefault()}>
        <div className={styles.content}>
          <TitleLargeEmphasized as="h1">{pageTitle}</TitleLargeEmphasized>
          <BodyMedium className={styles.formInstruction} aria-hidden="true" color={colors.slate}>
            <span style={{ color: colors.dragon }}>*</span> Email is required.
          </BodyMedium>
          <div className={styles.emailContainer}>
            <TextInput
              id="emailInput"
              name="emailInput"
              label="Email Address"
              type="email"
              value={email}
              autoComplete="email"
              onChange={onEmailInputChange}
              onKeyDown={checkKeyAndSubmitEmail}
              error={hasError}
              helperText={hasError && emailErrorText}
              aria-describedby={FORM_INSTRUCTIONS_ID}
              required
              inputProps={{
                'aria-required': true,
              }}
            />
          </div>
        </div>
        <div className={styles.footer}>
          <ButtonPrimaryLarge fullWidth onClick={onSubmit}>
            Log In
          </ButtonPrimaryLarge>
          <BodyMedium className={styles.secondaryCTA} color={colors.slate}>
            New to Peachjar?{' '}
            <button type="button" className={styles.link} onClick={onSignUp}>
              Sign Up
            </button>
          </BodyMedium>
        </div>
      </form>
    </main>
  );
};
